import React, { FC, ReactChild, Suspense, useMemo } from 'react';
import classNames from 'classnames';
import { Icon, Tooltip, Image } from '@whitesharx/starfall-frontend-ui-kit';
import { t } from '@whitesharx/starfall-frontend-ttranslate';
import {
  GameResultTitle,
  PlayerGeneralInfo,
  usePlayerQuery,
  formatPlayerData,
  resultGetterMap,
  ErrorBoundary,
} from '@whitesharx/starfall-frontend-core';

import {
  containerStyles,
  accuracyTitleStyles,
  accuracyValueStyles,
  iconStyles,
  imageContainerStyles,
  accuracyInfoStyles,
  resultTextStyles,
  cupImageStyles,
  drawImageStyles,
  tooltipStyles,
} from './styles.css';
import {
  analyzeGameAction,
  analysisProgressAtom,
  getAnalysisGameAction,
  selectMoveAction,
  gameAnalyzedAtom,
} from '../../model';
import { useAction, useAtom } from '@reatom/npm-react';
import { ChartLazy } from '../Chart';
import { contentPaddingStyles } from '../../styles.css';
import { Grades } from '../Grades';
import { Progress } from '../Progress';
import { getPlayersAccuracy, getSummary } from '../../utils';
import { isMobileBrowser } from '~/src/constants/env';

type Props = {
  className: string;
  analyzeButton?: ReactChild;
};

const ReviewTab: FC<Props> = ({ className, analyzeButton }) => {
  const { data: playerData } = usePlayerQuery();

  const [game] = useAtom(getAnalysisGameAction.dataAtom);
  const [gameAnalyzed] = useAtom(gameAnalyzedAtom);
  const [analyzingStatus] = useAtom(analyzeGameAction.statusesAtom);
  const [analyzeGameController] = useAtom(analyzeGameAction.abortControllerAtom);
  const [analysisProgress] = useAtom(analysisProgressAtom);
  const selectMove = useAction(selectMoveAction);

  const analyzing = analyzingStatus.isPending && !analyzeGameController?.signal.aborted;

  const result = useMemo(() => {
    if (game && playerData?.player) {
      const isWhitePlayer = playerData.player.id === game.whitePlayerId;
      const isWinner = game.winner?.playerId === playerData.player.id;
      const { title } = resultGetterMap[game.winner?.reason!](isWinner);
      const summary = getSummary(game, title, playerData.player.id);
      const accuracy = getPlayersAccuracy(game);

      return {
        player: formatPlayerData(isWhitePlayer ? game.whitePlayer : game.blackPlayer),
        enemy: formatPlayerData(isWhitePlayer ? game.blackPlayer : game.whitePlayer),
        title,
        accuracy: {
          player: isWhitePlayer ? accuracy.white : accuracy.black,
          enemy: isWhitePlayer ? accuracy.black : accuracy.white,
        },
        summary,
      };
    }
  }, [playerData?.player.id, game]);

  const { title, player, enemy, accuracy, summary } = result || {};

  const isWin = title === GameResultTitle.victory;
  const isLose = title === GameResultTitle.defeat;

  const renderImage = () => {
    if (title === GameResultTitle.draw || title === GameResultTitle.stalemate) {
      return <Image src="/assets/images/draw.webp" className={drawImageStyles} />;
    }
    if (title === GameResultTitle.defeat) {
      return <Image src="/assets/images/cup-crash.webp" className={cupImageStyles} />;
    }
    return <Image src="/assets/images/count-cup.webp" className={cupImageStyles} />;
  };

  return (
    <article className={classNames(contentPaddingStyles, className)} aria-label="review">
      <div className={containerStyles}>
        <PlayerGeneralInfo {...player} isWin={isWin} setName={undefined} flagPlacement="name" />
        <div className={imageContainerStyles}>
          {title && renderImage()}
          <span className={resultTextStyles}>{`${Number(isWin)} - ${Number(isLose)}`}</span>
        </div>
        <PlayerGeneralInfo {...enemy} isWin={isLose} setName={undefined} flagPlacement="name" />
      </div>
      <div className={containerStyles}>
        <span className={accuracyValueStyles}>
          {typeof accuracy?.player === 'number' && gameAnalyzed ? `${accuracy.player}%` : '??'}
        </span>
        <span className={accuracyTitleStyles}>
          {t('analysis.accuracy')}{' '}
          <Tooltip
            content={t('analysis.accuracy-info')}
            trigger={isMobileBrowser ? 'click' : 'hover'}
            contentClassName={accuracyInfoStyles}
            tooltipClassName={tooltipStyles}
            closeOnTimeout={false}
            placement="top"
          >
            <Icon name="info" color="purple100" className={iconStyles} size="20px" />
          </Tooltip>
        </span>
        <span className={accuracyValueStyles}>
          {typeof accuracy?.enemy === 'number' && gameAnalyzed ? `${accuracy.enemy}%` : '??'}
        </span>
      </div>

      {analyzeButton}

      {analyzing && <Progress value={analysisProgress} />}

      {gameAnalyzed && (
        <>
          <ErrorBoundary fallBack={<div />}>
            <Suspense fallback={<div />}>
              <ChartLazy
                moves={game?.moves || []}
                title={summary?.title}
                subtitle={summary?.desc}
                onPointClick={selectMove}
              />
            </Suspense>
          </ErrorBoundary>

          <Grades moves={game?.moves} />
        </>
      )}
    </article>
  );
};

export default ReviewTab;
